import { Clock, UserX } from 'lucide-react';
import type { Client } from '@/features/client-relationships';
import { useRemoveClient } from '@/features/client-relationships';
import { formatLastLogin } from '@/shared/utils/date-formatter';
import { getInitials } from '../utils/clientDisplay';

type ConnectedClientsListProps = {
  clients: (Client & { type?: 'CONNECTED' })[];
};

export function ConnectedClientsList({
  clients,
}: ConnectedClientsListProps) {
  const removeClientMutation = useRemoveClient();

  const handleRemove = async (clientId: string, displayName: string) => {
    if (confirm(`Are you sure you want to remove ${displayName} from your client list?`)) {
      try {
        await removeClientMutation.mutateAsync(clientId);
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to remove client.');
      }
    }
  };

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-3">
        Connected Clients ({clients.length})
      </h4>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {clients.map((client) => {
          const fullName = [client.firstName, client.lastName].filter(Boolean).join(' ');

          return (
            <div
              key={client.id}
              className="p-4 border border-gray-200 rounded-lg hover:border-[#026857] transition-colors"
            >
              <div className="flex items-start gap-3 mb-3">
                {client.profilePhoto ? (
                  <img
                    src={client.profilePhoto}
                    alt={fullName}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#026857]/10 flex items-center justify-center">
                    <span className="text-[#026857] font-semibold text-sm">
                      {getInitials(fullName)}
                    </span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 truncate">
                    {fullName}
                  </h4>
                  <p className="text-xs text-gray-500 truncate">
                    {client.email}
                  </p>
                </div>
                <button
                  onClick={() => handleRemove(client.id, fullName)}
                  disabled={removeClientMutation.isPending}
                  aria-label={`Remove ${fullName}`}
                  data-testid="remove-client-button"
                  className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <UserX className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-xs">
                  Last login:{' '}
                  <span className="font-medium">
                    {formatLastLogin(client.lastLoginAt ?? null)}
                  </span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
